import { useCallback, useEffect, useRef, useState } from "react";
import { clearConsentTerminal, fetchPatientProfile, requestPatientConsent, saveDraftScreening, staffAcceptRegisterConsent } from "../../api/services/clinic/patientManagement";
import { fetchMedicalCertificates, searchPackages, getPackageCategories } from "../../api/services/clinic/medications";
import Button from "../../components/Button";
import Checkbox from "../../components/Checkbox";
import Chip from "../../components/Chip";
import Icon from "../../components/Icon";
import InputField from "../../components/InputField";
import RadioButton from "../../components/RadioButton";
import { useToast } from "../../contexts/ToastContext";
import Indicator from "../../components/Indicator";
import realtimeClient from "../../realtime/client";
import { formatThaiDate, formatTimeFromDate } from "../../utils/date";
import * as regex from "../../utils/regexUtils";
import NoPatientConsentModal from "./NoPatientConsentModal";
import { trimObjectValues } from "../../utils/form";
import {
    buildScreeningPayload,
    isVitalSignsRequiredForScreening,
    isWeightMeasurementStale,
} from "../../utils/screening";
import CollapsibleSection from "../../components/CollapsibleSection";

const NOTE_TO_DOCTOR_MAX_LENGTH = 500;
const WEIGHT_STALE_MESSAGE = "น้ำหนักบันทึกเกิน 3 เดือน";

const DEFAULT_FORM = {
    chiefComplaint: "",
    noteToDoctor: "",
    diastolic: "",
    systolic: "",
    heartRate: "",
    temperature: "",
    weight: "",
    height: "",
    congenitalDiseaseDetail: "",
    hasDrugAllergy: "no",
    drugAllergyDetail: [],
    hasSmoking: "no",
    smokingDetail: "",
    hasDrinking: "no",
    drinkingDetail: "",
    isPregnant: "no",
    selectedMedicalCertificates: [],
    treatmentPackages: [],
    isLabTest: false,
    isLabResultAppointment: false,
    isTelemedQueue: false,
};

const EMPTY_INITIAL_VALUES = {};

const STEPS = [
    { id: 1, label: "ระบุประเภทผู้ป่วย" },
    { id: 2, label: "ข้อมูลผู้ป่วย" },
    { id: 3, label: "การคัดกรองเบื้องต้น" },
];

const CURRENT_STEP = 3;

/**
 * ScreeningModal
 *
 * React translation of `templates/his/staff/screening_modal.html`.
 * Renders a modal form for collecting vital signs and lifestyle information
 * before a patient enters the queue.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 *   Controls the visibility of the modal. When `false`, the component renders `null`.
 *
 * @param {function} props.onCancel
 *   Callback fired when the user clicks the cancel button. Typically used to hide the modal.
 *
 * @param {function} props.onSave
 *   Callback fired when the user clicks the save button. Receives the full screening form object:
 *   {
 *     chiefComplaint,
 *     noteToDoctor,
 *     diastolic,
 *     systolic,
 *     heartRate,
 *     temperature,
 *     weight,
 *     height,
 *     hasCongenitalDisease,
 *     congenitalDiseaseDetail,
 *     hasDrugAllergy,
 *     drugAllergyDetail,
 *     hasSmoking,
 *     smokingDetail,
 *     hasDrinking,
 *     drinkingDetail,
 *     selectedMedicalCertificates,
 *     isLabTest,
 *     isLabResultAppointment,
 *   }
 *
 * @param {object} [props.initialValues]
 *   Optional default values for the form, useful when editing an existing screening record.
 *
 * Example usage inside a page component:
 *
 * ```jsx
 * const [open, setOpen] = useState(false);
 *
 * return (
 *   <>
 *     <Button text="เปิดหน้าต่างคัดกรอง" onClick={() => setOpen(true)} />
 *     <ScreeningModal
 *       isOpen={open}
 *       onCancel={() => setOpen(false)}
 *       onSave={(data) => {
 *         console.log("Screening data", data);
 *         setOpen(false);
 *       }}
 *     />
 *   </>
 * );
 * ```
 */
export default function ScreeningModal({
    isOpen,
    onCancel,
    onSave,
    onSaveDraftSuccess,
    initialValues = EMPTY_INITIAL_VALUES,
    patient,
    patientId,
    readOnly = false,
}) {
    const DRUG_ALLERGY_SUGGESTIONS = [
        ["Penicillins", "Penicillins"],
        ["Cephalosporins", "Cephalosporins"],
        ["Sulfonamides", "Sulfonamides"],
        ["TMP-SMX", "TMP-SMX"],
        ["Dapsone", "Dapsone"],
        ["Macrolides", "Macrolides"],
        ["Tetracyclines", "Tetracyclines"],
        ["Quinolones", "Quinolones"],
        ["Clindamycin", "Clindamycin"],
        ["Allopurinol", "Allopurinol"],
        ["Paracetamol", "Paracetamol"],
        ["NSAIDs", "NSAIDs"],
    ];
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [drugAllergyInput, setDrugAllergyInput] = useState("");
    const [form, setForm] = useState(DEFAULT_FORM);
    const [errors, setErrors] = useState({});
    const [medicalCertificates, setMedicalCertificates] = useState(null);
    const [patientInfo, setPatientInfo] = useState(null);
    const [weightLastMeasuredAt, setWeightLastMeasuredAt] = useState(null);
    const [showNoConsentModal, setShowNoConsentModal] = useState(false);
    const { showSuccessToast, showErrorToast } = useToast();

    // Package selector states
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [packageSearchQuery, setPackageSearchQuery] = useState("");
    const [packages, setPackages] = useState([]);
    const [packagesLoading, setPackagesLoading] = useState(false);
    const [categories, setCategories] = useState([]);

    const refreshPatientInfo = useCallback(async () => {
        if (!patientId) return null;

        const profile = await fetchPatientProfile(patientId);
        const nextPatientInfo = profile?.patient_info ?? null;
        setPatientInfo(nextPatientInfo);
        setWeightLastMeasuredAt(profile?.health_info?.weight_last_measured_at ?? null);
        return nextPatientInfo;
    }, [patientId]);

    const refreshAcceptedConsent = useCallback(async () => {
        const latestPatientInfo = await refreshPatientInfo();
        return Boolean(
            latestPatientInfo?.accepted_consent ??
            latestPatientInfo?.patient_info?.accepted_consent
        );
    }, [refreshPatientInfo]);

    async function onDrugAllergySearch(query) {
        const q = String(query || "").toLowerCase();
        const selected = form.drugAllergyDetail || [];
        return DRUG_ALLERGY_SUGGESTIONS.filter(([, label]) =>
            label.toLowerCase().includes(q) && !selected.includes(label)
        );
    }

    useEffect(() => {
        let isActive = true;

        if (!isOpen || !patientId) {
            setPatientInfo(null);
            setWeightLastMeasuredAt(null);
            return () => {
                isActive = false;
            };
        }

        setPatientInfo(patient?.patient_info ?? null);
        setWeightLastMeasuredAt(patient?.health_info?.weight_last_measured_at ?? null);
        fetchPatientProfile(patientId)
            .then((profile) => {
                if (isActive) {
                    const nextPatientInfo = profile?.patient_info ?? null;
                    const nextWeightLastMeasuredAt = profile?.health_info?.weight_last_measured_at ?? null;
                    setPatientInfo(nextPatientInfo);
                    setWeightLastMeasuredAt(nextWeightLastMeasuredAt);

                    if (isWeightMeasurementStale(nextWeightLastMeasuredAt)) {
                        // ฟอร์มอาจถูก prefill น้ำหนักเก่าไว้จาก initialValues/patient
                        // (ค่าจากรอบก่อน ไม่ใช่ค่าที่เพิ่งชั่งใหม่) ต้องล้างเพื่อบังคับกรอกใหม่จริงๆ
                        const prefilledWeight = initialValues?.weight ?? patient?.health_info?.weight ?? null;
                        setForm((prev) =>
                            prefilledWeight !== null && String(prev.weight) === String(prefilledWeight)
                                ? { ...prev, weight: "" }
                                : prev
                        );
                    }

                    if (nextPatientInfo) {
                        setForm((prev) => {
                            const congenitalDiseaseDetail = prev.congenitalDiseaseDetail || nextPatientInfo.congenital_disease_details || "";

                            const hasDrugAllergy = prev.hasDrugAllergy !== "no"
                                ? prev.hasDrugAllergy
                                : (Array.isArray(nextPatientInfo.drug_allergies_details) && nextPatientInfo.drug_allergies_details.length > 0 ? "yes" : "no");

                            const drugAllergyDetail = (prev.drugAllergyDetail && prev.drugAllergyDetail.length > 0)
                                ? prev.drugAllergyDetail
                                : (nextPatientInfo.drug_allergies_details || []);

                            const hasSmoking = prev.hasSmoking !== "no"
                                ? prev.hasSmoking
                                : (nextPatientInfo.smoking_status && nextPatientInfo.smoking_status !== "ไม่สูบ" ? "yes" : "no");

                            const smokingDetail = prev.smokingDetail
                                ? prev.smokingDetail
                                : (nextPatientInfo.smoking_status && nextPatientInfo.smoking_status !== "ไม่สูบ" ? nextPatientInfo.smoking_status : "");

                            const hasDrinking = prev.hasDrinking !== "no"
                                ? prev.hasDrinking
                                : (nextPatientInfo.alcohol_consumption && nextPatientInfo.alcohol_consumption !== "ไม่ดื่ม" ? "yes" : "no");

                            const drinkingDetail = prev.drinkingDetail
                                ? prev.drinkingDetail
                                : (nextPatientInfo.alcohol_consumption && nextPatientInfo.alcohol_consumption !== "ไม่ดื่ม" ? nextPatientInfo.alcohol_consumption : "");

                            const isPregnant = prev.isPregnant !== "no"
                                ? prev.isPregnant
                                : (nextPatientInfo.is_pregnant === true ? "yes" : "no");

                            return {
                                ...prev,
                                congenitalDiseaseDetail,
                                hasDrugAllergy,
                                drugAllergyDetail,
                                hasSmoking,
                                smokingDetail,
                                hasDrinking,
                                drinkingDetail,
                                isPregnant,
                            };
                        });
                    }
                }
            })
            .catch((err) => {
                if (isActive) {
                    console.error("Failed to fetch patient profile", err);
                }
            });

        return () => {
            isActive = false;
        };
    }, [isOpen, patient, patientId, initialValues?.weight]);

    const isConsentRequestedRef = useRef(false);
    const isStaffAcceptingRef = useRef(false);
    const isClosingRef = useRef(!isOpen);
    const consentRequestPromiseRef = useRef(null);

    useEffect(() => {
        isClosingRef.current = !isOpen;
        if (!isOpen) {
            isConsentRequestedRef.current = false;
        }
        isStaffAcceptingRef.current = false;
    }, [isOpen, patientId]);

    useEffect(() => {
        async function autoRequestConsent() {
            if (readOnly) return;
            if (isOpen && patientInfo && !patientInfo.accepted_consent && !isConsentRequestedRef.current) {
                isConsentRequestedRef.current = true;
                try {
                    if (await refreshAcceptedConsent()) {
                        return;
                    }
                } catch (err) {
                    console.error("Failed to refresh patient profile before consent request", err);
                }

                if (isClosingRef.current) return;

                let requestPromise = null;
                try {
                    requestPromise = requestPatientConsent(patientId);
                    consentRequestPromiseRef.current = requestPromise;
                    await requestPromise;
                    if (!isClosingRef.current) {
                        showSuccessToast("เรียกหน้าหนังสือยินยอมสำเร็จ");
                    }
                } catch (err) {
                    console.error("เรียกหน้าหนังสือยินยอมไม่สำเร็จ", err);
                } finally {
                    if (consentRequestPromiseRef.current === requestPromise) {
                        consentRequestPromiseRef.current = null;
                    }
                }
            }
        }

        autoRequestConsent();
    }, [isOpen, patientId, patientInfo, refreshAcceptedConsent, showSuccessToast, readOnly]);

    useEffect(() => {
        if (!isOpen) return;

        const handleConsentUpdate = (payload) => {
            const data = payload.data;
            if (data && String(data.patient_id) === String(patientId)) {
                setPatientInfo(prev => {
                    const wasAccepted = Boolean(prev?.accepted_consent);
                    const isAcceptedNow = Boolean(data?.accepted_consent);
                    if (!wasAccepted && isAcceptedNow) {
                        if (!isStaffAcceptingRef.current) {
                            showSuccessToast("ผู้ป่วยยอมรับหนังสือยินยอมแล้ว");
                        }
                    }
                    return {
                        ...prev,
                        accepted_consent: data.accepted_consent,
                        consent_accepted_at: data.consent_accepted_at
                    };
                });
                isStaffAcceptingRef.current = false;
            }
        };

        const unsubscribe = realtimeClient.subscribe("patient_consent_updated", handleConsentUpdate);
        return () => unsubscribe();
    }, [isOpen, patientId, showSuccessToast]);

    useEffect(() => {
        async function loadData() {
            try {
                const [certResponse, catsResponse] = await Promise.all([
                    fetchMedicalCertificates(),
                    getPackageCategories().catch(err => {
                        console.error("Failed to fetch package categories", err);
                        return null;
                    })
                ]);

                const certs = Array.isArray(certResponse?.results) ? certResponse.results : (Array.isArray(certResponse) ? certResponse : []);
                setMedicalCertificates(certs);

                if (catsResponse) {
                    const cats = Array.isArray(catsResponse?.categories) ? catsResponse.categories : (Array.isArray(catsResponse) ? catsResponse : []);
                    if (cats.length > 0) {
                        setCategories(cats);
                    }
                }
            } catch (error) {
                console.error("Failed to load screening data", error);
            }
        }

        loadData();
    }, []);

    // Fetch packages dynamically based on search query and category
    useEffect(() => {
        let isMounted = true;
        async function loadPackages() {
            setPackagesLoading(true);
            try {
                const response = await searchPackages(packageSearchQuery, {
                    category: selectedCategory || "",
                    ordering: "frequency",
                    pageSize: 50,
                });
                if (isMounted) {
                    setPackages(Array.isArray(response?.results) ? response.results : []);
                }
            } catch (err) {
                console.error("Failed to load packages", err);
            } finally {
                if (isMounted) {
                    setPackagesLoading(false);
                }
            }
        }

        const handler = setTimeout(() => {
            loadPackages();
        }, 300);

        return () => {
            isMounted = false;
            clearTimeout(handler);
        };
    }, [packageSearchQuery, selectedCategory]);

    useEffect(() => {
        if (!isOpen) return;

        const initialForm = {
            ...DEFAULT_FORM,
            ...initialValues,
        };
        setForm(initialForm);
        setErrors({});
    }, [isOpen, initialValues]);

    function toggleMedicalCertificateCheckbox(medicalCertificate) {
        setForm(prev => {
            const prevList = prev.selectedMedicalCertificates || [];
            const exists = prevList.find(x => (x.medical_certificate_id ?? x.id) === medicalCertificate.id);

            const newList = exists
                ? prevList.filter(x => (x.medical_certificate_id ?? x.id) !== medicalCertificate.id)
                : [...prevList, medicalCertificate];

            return {
                ...prev,
                selectedMedicalCertificates: newList
            };
        });
    }

    function handleChange(field, value) {
        setForm((prev) => ({ ...prev, [field]: value }));

        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    }

    function handleAddDrugAllergy(value) {
        if (!value) return;
        const currentList = Array.isArray(form.drugAllergyDetail) ? form.drugAllergyDetail : [];
        if (!currentList.includes(value)) {
            handleChange("drugAllergyDetail", [...currentList, value]);
        }
        setDrugAllergyInput("");
    }

    function handleRemoveDrugAllergy(value) {
        const currentList = Array.isArray(form.drugAllergyDetail) ? form.drugAllergyDetail : [];
        handleChange("drugAllergyDetail", currentList.filter(item => item !== value));
    }

    function handleAddPackage(value) {
        if (!value) return;
        const currentList = Array.isArray(form.treatmentPackages) ? form.treatmentPackages : [];
        if (!currentList.includes(value)) {
            handleChange("treatmentPackages", [...currentList, value]);
        }
    }

    function handleRemovePackage(value) {
        const currentList = Array.isArray(form.treatmentPackages) ? form.treatmentPackages : [];
        handleChange("treatmentPackages", currentList.filter(item => item !== value));
    }

    // UC1: อายุ >= 7 ปี (หรือไม่มีวันเกิด) + เลือกใบรับรองแพทย์ 5 โรค → บังคับ BP/ชีพจร
    const requireVitalSigns = isVitalSignsRequiredForScreening({
        birthDate: patientInfo?.birth_date,
        selectedMedicalCertificates: form.selectedMedicalCertificates,
    });
    const missingRequiredVitalSigns =
        requireVitalSigns && (!form.systolic || !form.diastolic || !form.heartRate);

    // น้ำหนักที่บันทึกล่าสุดเกิน 90 วัน (หรือไม่เคยบันทึก) → บังคับกรอกใหม่
    const isWeightStale = isWeightMeasurementStale(weightLastMeasuredAt);
    const missingWeightRefresh = isWeightStale && !form.weight;
    // A brand-new patient who has never had a weight on file still needs one
    // entered (see hasBlockingVitalSignsErrors below), but there's nothing to
    // warn them "went stale" about, so no message is shown for that case.
    const weightRefreshMessage = weightLastMeasuredAt ? WEIGHT_STALE_MESSAGE : undefined;

    const hasBlockingVitalSignsErrors = missingRequiredVitalSigns || missingWeightRefresh;

    async function handleSubmit() {
        if (isSubmitting) return;

        let hasAcceptedConsent = Boolean(patientInfo?.accepted_consent);
        if (!hasAcceptedConsent) {
            try {
                hasAcceptedConsent = await refreshAcceptedConsent();
            } catch (err) {
                console.error("Failed to refresh patient profile before submit", err);
                setShowNoConsentModal(true);
                return;
            }
        }

        if (!hasAcceptedConsent) {
            setShowNoConsentModal(true);
            return;
        }

        const newErrors = {};


        if (form.hasSmoking === "yes" && !form.smokingDetail) {
            newErrors.smokingDetail = "กรุณากรอกข้อมูล";
        }
        if (form.hasDrinking === "yes" && !form.drinkingDetail) {
            newErrors.drinkingDetail = "กรุณากรอกข้อมูล";
        }

        if (requireVitalSigns) {
            if (!form.systolic) newErrors.systolic = "กรุณากรอกความดันตัวบน";
            if (!form.diastolic) newErrors.diastolic = "กรุณากรอกความดันตัวล่าง";
            if (!form.heartRate) newErrors.heartRate = "กรุณากรอกชีพจร";
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsSubmitting(true);
        try {
            if (typeof onSave === "function") {
                const cleanedForm = trimObjectValues(form);
                await onSave(cleanedForm);
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    function handleIntegerInput(field, rawValue) {
        if (rawValue === "" || /^\d+$/.test(rawValue)) {
            handleChange(field, rawValue);
        }
    }

    function handleDecimalInput(field, rawValue) {
        const normalized = rawValue.replace(/,/g, ".");
        if (normalized === "" || /^\d*(\.\d*)?$/.test(normalized)) {
            handleChange(field, normalized);
        }
    }

    function buildHeader() {
        return (
            <div className="gap-5 flex flex-col"><div className="flex flex-row justify-between">
                <div className="w-full gap-2 flex flex-col">
                    <p className="font-ui font-header text-title text-main-typography-system-default">
                        การลงทะเบียนผู้ป่วย
                    </p>
                    <p className="font-ui font-body text-body-small text-main-typography-system-description">
                        ลงทะเบียนหรือระบุผู้ป่วยก่อนการคัดกรอง
                    </p>
                </div>
                <button
                    className="text-main-typography-system-caption"
                    onClick={handleCancel}
                    aria-label="ปิด"
                >
                    <Icon name="x" size="md" />
                </button>
            </div>
                <Indicator
                    steps={STEPS}
                    currentStep={CURRENT_STEP}
                />
            </div>
        );
    }

    function buildComplaintSection() {
        return (
            <div
                className={`w-full flex flex-col${readOnly ? " pointer-events-none opacity-70 select-none" : ""}`}
                inert={readOnly}
            >
                <div className="w-full gap-5 flex">
                    <div className="w-full flex flex-1 flex-col">
                        <InputField
                            name="chief_complaint"
                            label="อาการที่มาพบแพทย์"
                            type="textarea"
                            textareaRows={2}
                            placeholder="ระบุอาการที่มาพบแพทย์ เช่น ปวดศีรษะ ไอ มีไข้ ปวดท้อง"
                            value={form.chiefComplaint}
                            onChange={(e) => handleChange("chiefComplaint", e.target.value)}
                            disabled={readOnly}
                        />
                    </div>
                    <div className="w-full flex flex-1 flex-col">
                        <InputField
                            name="note_to_doctor"
                            label="หมายเหตุแจ้งแพทย์"
                            type="textarea"
                            textareaRows={2}
                            maxLength={NOTE_TO_DOCTOR_MAX_LENGTH}
                            placeholder="ระบุหมายเหตุที่ต้องการแจ้งแพทย์เพิ่มเติม"
                            value={form.noteToDoctor}
                            onChange={(e) => handleChange("noteToDoctor", e.target.value)}
                            disabled={readOnly}
                        />
                    </div>
                </div>
                <div className="gap-3 flex flex-col">
                    <p className="font-ui font-header text-body-base text-main-typography-system-default">เหตุผลอื่นๆ</p>
                    <div className="flex flex-wrap items-center gap-5">
                        <Checkbox
                            id="isLabTest"
                            label="ตรวจแลป"
                            checked={form.isLabTest}
                            onChange={(e) => {
                                setForm((prev) => ({
                                    ...prev,
                                    isLabTest: e.target.checked,
                                    isLabResultAppointment: e.target.checked ? false : prev.isLabResultAppointment
                                }));
                            }}
                        />
                        <Checkbox
                            id="isLabResultAppointment"
                            label="นัดหมายฟังผลแลป"
                            checked={form.isLabResultAppointment}
                            onChange={(e) => {
                                setForm((prev) => ({
                                    ...prev,
                                    isLabResultAppointment: e.target.checked,
                                    isLabTest: e.target.checked ? false : prev.isLabTest
                                }));
                            }}
                        />
                        <Checkbox
                            id="isTelemedQueue"
                            label="เทเลเมดิซีน (Telemedicine)"
                            checked={form.isTelemedQueue}
                            onChange={(e) => handleChange("isTelemedQueue", e.target.checked)}
                        />
                    </div>
                </div>
            </div>
        );
    }

    function buildPackagesCollapsibleSection() {
        const selectedCount = (form.treatmentPackages || []).length;

        return (
            <div className="w-full">
                <CollapsibleSection
                    title="แพ็กเกจ"
                    badgeText={`${selectedCount} รายการ`}
                    badgeColor={selectedCount > 0 ? "cerulean_blue" : "oslo"}
                    canToggle={false}
                >
                    <div
                        className={`w-full flex flex-col gap-3${readOnly ? " pointer-events-none opacity-70 select-none" : ""}`}
                        inert={readOnly}
                    >
                        {/* Filters Row */}
                        <div className="flex flex-row justify-between items-center">
                            <div className="flex flex-row items-center gap-3">
                                <span className="font-ui font-body text-body-base text-main-typography-system-caption">ตัวกรอง:</span>
                                {categories.map((cat) => (
                                    <Chip
                                        key={cat.value}
                                        label={cat.label}
                                        selected={selectedCategory === cat.value}
                                        onClick={() => setSelectedCategory(selectedCategory === cat.value ? null : cat.value)}
                                    />
                                ))}
                            </div>
                            <InputField
                                name="package_search"
                                type="search"
                                placeholder="ค้นหาแพ็กเกจ"
                                value={packageSearchQuery}
                                onChange={(e) => setPackageSearchQuery(e.target.value)}
                                allowClear={true}
                                className="w-[309px]"
                                disabled={readOnly}
                                alwaysShowPlaceholder
                            />
                        </div>

                        {/* Cards Grid */}
                        {packagesLoading && packages.length === 0 ? (
                            <div className="w-full h-[168px] flex items-center justify-center">
                                <p className="font-ui font-body text-body-small text-main-typography-system-description">กำลังโหลดแพ็กเกจ...</p>
                            </div>
                        ) : packages.length === 0 ? (
                            <div className="w-full h-[168px] flex items-center justify-center">
                                <p className="font-ui font-body text-body-small text-main-typography-system-description">ไม่พบแพ็คเกจ</p>
                            </div>
                        ) : (
                            <div className="w-full overflow-x-auto custom-horizontal-scrollbar pb-3">
                                <div className="grid grid-rows-2 grid-flow-col gap-3 auto-cols-[309px] min-w-full">
                                    {packages.map((pkg) => {
                                        const isSelected = (form.treatmentPackages || []).includes(pkg.id);
                                        const isUnavailable = pkg.status !== "available";
                                        const categoriesList = Array.isArray(pkg.categories)
                                            ? pkg.categories
                                            : (pkg.categories ? pkg.categories.split(',') : []);
                                        const categoryDisplay = categoriesList.map(cat => {
                                            const cleaned = cat.trim();
                                            return categories.find(c => c.value === cleaned)?.label || cleaned;
                                        }).join(' · ');

                                        return (
                                            <div
                                                key={pkg.id}
                                                onClick={() => {
                                                    if (isUnavailable) return;
                                                    if (isSelected) {
                                                        handleRemovePackage(pkg.id);
                                                    } else {
                                                        handleAddPackage(pkg.id);
                                                    }
                                                }}
                                                className={`flex flex-row items-start p-4 gap-3 rounded-lg border transition-all select-none ${isUnavailable
                                                    ? "bg-creation-neutral-container-surface-disabled border-main-border-system-default opacity-60 cursor-not-allowed"
                                                    : "border-main-border-system-default"
                                                    }`}
                                            >
                                                {/* Checkbox */}
                                                <div className="flex-shrink-0 pointer-events-none">
                                                    <Checkbox
                                                        id={`package-checkbox-${pkg.id}`}
                                                        checked={isSelected}
                                                        enabled={!isUnavailable}
                                                        onChange={() => { }}
                                                    />
                                                </div>

                                                {/* Card Details */}
                                                <div className="flex flex-col flex-1 min-w-0">
                                                    <span className="font-ui font-body text-caption text-main-typography-system-description truncate">
                                                        {categoryDisplay || "\u00a0"}
                                                    </span>
                                                    <span className="font-ui font-header text-body-base text-main-typography-system-default truncate">
                                                        {pkg.name}
                                                    </span>
                                                    <span className="font-ui font-body text-caption text-main-typography-accented-primary">
                                                        {Number(pkg.price).toLocaleString()} บาท
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </CollapsibleSection>
            </div>
        );
    }

    function handleBodyTemperatureInput(value) {
        if (value === "" || regex.physiologicalTempRegex.test(value)) {
            handleDecimalInput('temperature', value);
        }
    }

    function handleWeightInput(value) {
        if (value === "" || regex.weightRegex.test(value)) {
            handleDecimalInput('weight', value);
        }
    }

    function handleHeightInput(value) {
        if (value === "" || regex.heightRegex.test(value)) {
            handleDecimalInput('height', value);
        }
    }

    function buildVitalSignsSection() {
        return (
            <div
                className={readOnly ? "pointer-events-none opacity-70 select-none" : undefined}
                inert={readOnly}
            >
                <p className="font-ui font-header text-body-base text-main-typography-system-default">สัญญาณชีพ</p>
                <div className="gap-5 flex flex-row items-start">
                    <div className="gap-3 flex flex-[5] flex-col">
                        <p className="font-ui font-header text-body-base text-main-typography-system-default">
                            ความดันโลหิต (mmHg){requireVitalSigns && <span className="text-helper-error"> *</span>}
                        </p>
                        <div className="flex flex-row flex-1 items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <InputField
                                    name="systolic"
                                    placeholder="120"
                                    value={form.systolic}
                                    inputMode="numeric"
                                    onChange={(e) =>
                                        handleIntegerInput("systolic", e.target.value)
                                    }
                                    maxLength={3}
                                    disabled={readOnly}
                                    isError={Boolean(errors.systolic)}
                                />
                            </div>
                            <p className="px-2 py-3 font-ui font-body text-body-base text-main-typography-system-caption">/</p>
                            <div className="flex-1 min-w-0">
                                <InputField
                                    name="diastolic"
                                    placeholder="80"
                                    value={form.diastolic}
                                    inputMode="numeric"
                                    onChange={(e) =>
                                        handleIntegerInput("diastolic", e.target.value)
                                    }
                                    maxLength={3}
                                    disabled={readOnly}
                                    isError={Boolean(errors.diastolic)}
                                />
                            </div>
                        </div>
                        {/* Rendered here (spanning the full BP column) instead of inside the
                            narrow systolic InputField, which would wrap a long message into
                            ~7 lines and push the diastolic field down with it. */}
                        {(errors.systolic || errors.diastolic) && (
                            <div className="text-helper text-helper-error">
                                <Icon name="x-circle" size="sm" />
                                <p className="px-2">{errors.systolic || errors.diastolic}</p>
                            </div>
                        )}
                    </div>
                    <div className="flex-[5]">
                        <InputField
                            name="heartRate"
                            label={<>การเต้นของหัวใจ (bpm){requireVitalSigns && <span className="text-helper-error"> *</span>}</>}
                            placeholder="72"
                            value={form.heartRate}
                            inputMode="numeric"
                            onChange={(e) =>
                                handleIntegerInput("heartRate", e.target.value)
                            }
                            maxLength={3}
                            disabled={readOnly}
                            errorText={errors.heartRate}
                        />
                    </div>
                    <div className="flex-[5]">
                        <InputField
                            name="temperature"
                            label="อุณหภูมิร่างกาย (°C)"
                            placeholder="36.5"
                            value={form.temperature}
                            inputMode="decimal"
                            onChange={(e) =>
                                handleBodyTemperatureInput(e.target.value)
                            }
                            disabled={readOnly}
                        />
                    </div>
                    <div className="flex-[4]">
                        <InputField
                            name="weight"
                            label={<>น้ำหนัก (kg){isWeightStale && <span className="text-helper-error"> *</span>}</>}
                            placeholder="65"
                            value={form.weight}
                            inputMode="decimal"
                            onChange={(e) =>
                                handleWeightInput(e.target.value)
                            }
                            disabled={readOnly}
                            errorText={missingWeightRefresh ? weightRefreshMessage : errors.weight}
                        />
                    </div>
                    <div className="flex-[4]">
                        <InputField
                            name="height"
                            label="ส่วนสูง (cm)"
                            placeholder="165"
                            value={form.height}
                            inputMode="decimal"
                            onChange={(e) =>
                                handleHeightInput(e.target.value)
                            }
                            disabled={readOnly}
                        />
                    </div>
                </div>
            </div>
        );
    }


    function buildLifestyleRows() {
        const congenitalEmpty = !(form.congenitalDiseaseDetail || "").trim();
        const hasLifestyleHistory = !congenitalEmpty ||
            (form.hasDrugAllergy === "yes" && (form.drugAllergyDetail || []).length > 0) ||
            (form.hasSmoking === "yes" && (form.smokingDetail || "").trim() !== "") ||
            (form.hasDrinking === "yes" && (form.drinkingDetail || "").trim() !== "");

        return (
            <div className="w-full">
                <CollapsibleSection
                    title="ประวัติสุขภาพ"
                    badgeText={hasLifestyleHistory ? "มีประวัติ" : "ไม่มีประวัติ"}
                    badgeColor={hasLifestyleHistory ? "cerulean_blue" : "oslo"}
                    canToggle={false}
                >
                    <div
                        className={`w-full flex flex-col gap-5 pt-3${readOnly ? " pointer-events-none opacity-70 select-none" : ""}`}
                        inert={readOnly}
                    >
                        <div className="flex flex-row gap-5">
                            <div className="flex flex-col flex-1 gap-3">
                                <p className="font-ui font-header text-body-base text-main-typography-system-default">โรคประจำตัว</p>
                                <div className="flex flex-row gap-5">
                                    <div className="flex-1">
                                        <InputField
                                            name="congenitalDiseaseDetail"
                                            placeholder="มีโรคประจำตัวโปรดระบุ..."
                                            value={form.congenitalDiseaseDetail}
                                            type="textarea"
                                            textareaRows={2}
                                            onChange={(e) =>
                                                handleChange("congenitalDiseaseDetail", e.target.value)
                                            }
                                            disabled={readOnly}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col flex-1 gap-3">
                                <p className="font-ui font-header text-body-base text-main-typography-system-default">ประวัติแพ้ยา</p>
                                <div className="flex flex-row gap-5">
                                    <RadioButton
                                        id="hasDrugAllergy-no"
                                        name="hasDrugAllergy"
                                        value="no"
                                        label="ไม่มี"
                                        showBackground={false}
                                        checked={form.hasDrugAllergy === "no"}
                                        onChange={() => {
                                            handleChange("hasDrugAllergy", "no");
                                            handleChange("drugAllergyDetail", []);
                                        }}
                                    />
                                    <RadioButton
                                        id="hasDrugAllergy-yes"
                                        name="hasDrugAllergy"
                                        value="yes"
                                        label="มี"
                                        showBackground={false}
                                        checked={form.hasDrugAllergy === "yes"}
                                        onChange={() => handleChange("hasDrugAllergy", "yes")}
                                    />
                                    <div className="flex-1">
                                        <InputField
                                            name="drugAllergyInput"
                                            type="search"
                                            placeholder="ค้นหา..."
                                            disabled={readOnly || form.hasDrugAllergy === "no"}
                                            alwaysShowPlaceholder
                                            value={drugAllergyInput}
                                            suggestions={DRUG_ALLERGY_SUGGESTIONS}
                                            onSearch={onDrugAllergySearch}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setDrugAllergyInput(val);
                                                const selected = e.target.selectedSuggestionValue;
                                                if (selected) {
                                                    handleAddDrugAllergy(selected);
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddDrugAllergy(drugAllergyInput.trim());
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2">
                                    {Array.isArray(form.drugAllergyDetail) && form.drugAllergyDetail.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {form.drugAllergyDetail.map((item, index) => (
                                                <Chip
                                                    key={index}
                                                    label={item}
                                                    selected={true}
                                                    showCloseButton={true}
                                                    onClickCloseButton={() => handleRemoveDrugAllergy(item)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-5">
                            <div className="flex flex-col flex-1 gap-3">
                                <p className="font-ui font-header text-body-base text-main-typography-system-default">สูบบุหรี่</p>
                                <div className="flex flex-row gap-5">
                                    <RadioButton
                                        id="hasSmoking-no"
                                        name="hasSmoking"
                                        value="no"
                                        label="ไม่มี"
                                        checked={form.hasSmoking === "no"}
                                        onChange={() => {
                                            handleChange("hasSmoking", "no");
                                            handleChange("smokingDetail", "");
                                        }}
                                        showBackground={false}
                                    />
                                    <RadioButton
                                        id="hasSmoking-yes"
                                        name="hasSmoking"
                                        value="yes"
                                        label="มี"
                                        checked={form.hasSmoking === "yes"}
                                        onChange={() => handleChange("hasSmoking", "yes")}
                                        showBackground={false}
                                    />
                                    <div className="flex-1">
                                        <InputField
                                            name="smokingDetail"
                                            placeholder="ระบุความถี่..."
                                            type="select"
                                            choices={[["สูบบ้าง", "สูบบ้าง"], ["สูบประจำ", "สูบประจำ"]]}
                                            disabled={readOnly || form.hasSmoking === "no"}
                                            alwaysShowPlaceholder
                                            value={form.smokingDetail}
                                            onChange={(e) =>
                                                handleChange("smokingDetail", e.target.value)
                                            }
                                            errorText={errors.smokingDetail}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col flex-1 gap-3">
                                <p className="font-ui font-header text-body-base text-main-typography-system-default">ดื่มแอลกอฮอล์</p>
                                <div className="flex flex-row gap-5">
                                    <RadioButton
                                        id="hasDrinking-no"
                                        name="hasDrinking"
                                        value="no"
                                        label="ไม่มี"
                                        checked={form.hasDrinking === "no"}
                                        onChange={() => {
                                            handleChange("hasDrinking", "no");
                                            handleChange("drinkingDetail", "");
                                        }}
                                        showBackground={false}
                                    />
                                    <RadioButton
                                        id="hasDrinking-yes"
                                        name="hasDrinking"
                                        value="yes"
                                        label="มี"
                                        checked={form.hasDrinking === "yes"}
                                        onChange={() => handleChange("hasDrinking", "yes")}
                                        showBackground={false}
                                    />
                                    <div className="flex-1">
                                        <InputField
                                            name="drinkingDetail"
                                            placeholder="ระบุความถี่..."
                                            type="select"
                                            choices={[["ดื่มบ้าง", "ดื่มบ้าง"], ["ดื่มประจำ", "ดื่มประจำ"]]}
                                            disabled={readOnly || form.hasDrinking === "no"}
                                            alwaysShowPlaceholder
                                            value={form.drinkingDetail}
                                            onChange={(e) =>
                                                handleChange("drinkingDetail", e.target.value)
                                            }
                                            errorText={errors.drinkingDetail}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex min-w-40 flex-col gap-3">
                                <p className="font-ui font-header text-body-base text-main-typography-system-default">ตั้งครรภ์</p>
                                <div className="flex flex-row gap-5">
                                    <RadioButton
                                        id="isPregnant-no"
                                        name="isPregnant"
                                        value="no"
                                        label="ไม่"
                                        checked={form.isPregnant === "no"}
                                        onChange={() => handleChange("isPregnant", "no")}
                                        showBackground={false}
                                    />
                                    <RadioButton
                                        id="isPregnant-yes"
                                        name="isPregnant"
                                        value="yes"
                                        label="ใช่"
                                        checked={form.isPregnant === "yes"}
                                        onChange={() => handleChange("isPregnant", "yes")}
                                        showBackground={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </CollapsibleSection>
            </div>
        );
    }

    function buildMedicalCertificatesRow() {
        if (!medicalCertificates || medicalCertificates.length === 0) return null;

        const selectedCertCount = (form.selectedMedicalCertificates || []).length;

        return (
            <div className="w-full">
                <CollapsibleSection
                    title="ใบรับรองแพทย์"
                    badgeText={selectedCertCount > 0 ? "ต้องการ" : "ยังไม่ได้เลือก"}
                    badgeColor={selectedCertCount > 0 ? "cerulean_blue" : "oslo"}
                    canToggle={false}
                >
                    <div
                        className={`flex flex-row py-3 gap-3${readOnly ? " pointer-events-none opacity-70 select-none" : ""}`}
                        inert={readOnly}
                    >
                        {medicalCertificates.map(medicalCertificate => {
                            const isSelected = (form.selectedMedicalCertificates || []).some(
                                (x) => (x.medical_certificate_id ?? x.id) === medicalCertificate.id
                            );
                            return (
                                <div key={medicalCertificate.id}>
                                    <Checkbox
                                        id={medicalCertificate.id}
                                        label={medicalCertificate.name.replace("ใบรับรองแพทย์", "")}
                                        enabled={true}
                                        showBackground={false}
                                        checked={isSelected}
                                        onChange={() =>
                                            toggleMedicalCertificateCheckbox(medicalCertificate)
                                        }
                                    />
                                </div>
                            );
                        })}
                    </div>
                </CollapsibleSection>
            </div>
        );
    }

    async function handleRequestConsent() {
        if (!patientId) return;

        try {
            if (await refreshAcceptedConsent()) {
                showSuccessToast("ผู้ป่วยยอมรับหนังสือยินยอมแล้ว");
                return;
            }

            await requestPatientConsent(patientId);
            showSuccessToast("เรียกหน้าหนังสือยินยอมใหม่สำเร็จ");
        } catch (error) {
            try {
                if (await refreshAcceptedConsent()) {
                    showSuccessToast("ผู้ป่วยยอมรับหนังสือยินยอมแล้ว");
                    return;
                }
            } catch (refreshError) {
                console.error("Failed to refresh patient profile", refreshError);
            }
            showErrorToast("เรียกหน้าหนังสือยินยอมใหม่ไม่สำเร็จ", error);
        }
    }

    async function handleAcceptConsent() {
        if (!patientId) return;

        try {
            if (await refreshAcceptedConsent()) {
                showSuccessToast("ผู้ป่วยยอมรับหนังสือยินยอมแล้ว");
                return;
            }

            isStaffAcceptingRef.current = true;
            const response = await staffAcceptRegisterConsent(patientId);
            if (response.success || response.accepted_by === "staff") {
                showSuccessToast("เจ้าหน้าที่ยอมรับหนังสือยินยอมสำเร็จ");
                setPatientInfo(prev => ({
                    ...prev,
                    accepted_consent: true,
                    consent_accepted_at: response.consent_accepted_at || new Date().toISOString()
                }));
            }
        } catch (error) {
            isStaffAcceptingRef.current = false;
            try {
                if (await refreshAcceptedConsent()) {
                    showSuccessToast("ผู้ป่วยยอมรับหนังสือยินยอมแล้ว");
                    return;
                }
            } catch (refreshError) {
                console.error("Failed to refresh patient profile", refreshError);
            }
            showErrorToast("ไม่สามารถยอมรับหนังสือยินยอมได้", error);
        }
    }

    async function cancelPatientTerminalSession() {
        isClosingRef.current = true;

        const pendingConsentRequest = consentRequestPromiseRef.current;
        if (pendingConsentRequest) {
            try {
                await pendingConsentRequest;
            } catch {
                // The request error is handled by autoRequestConsent.
            }
        }

        if (!patientId) return;

        try {
            await clearConsentTerminal();
        } catch (err) {
            console.error("Failed to clear consent terminal", err);
        }
    }

    async function handleCancel() {
        await cancelPatientTerminalSession();

        if (typeof onCancel === "function") {
            onCancel();
        }
    }

    async function handleDraft() {
        const cleanedForm = trimObjectValues(form);
        const payload = buildScreeningPayload({
            patientId: patientId,
            formData: cleanedForm,
        });
        try {
            await saveDraftScreening(patientId, payload);
            showSuccessToast("บันทึกแบบร่างสำเร็จ");
            await cancelPatientTerminalSession();

            if (typeof onCancel === "function") {
                onCancel();
            }
            if (typeof onSaveDraftSuccess === "function") {
                onSaveDraftSuccess();
            }
        } catch (error) {
            showErrorToast("ไม่สามารถบันทึกแบบร่างได้", error);
        }
    }

    function buildConsentSection() {
        if (!patientInfo) return null;

        const { accepted_consent, consent_accepted_at } = patientInfo;

        if (accepted_consent) {
            const dateStr = formatThaiDate(consent_accepted_at, { includeYear: true });
            const timeStr = formatTimeFromDate(consent_accepted_at);

            return (
                <div className="flex flex-row px-4 py-3 rounded-md bg-main-background-accented-success">
                    <Icon name="check-circle" size="md" className="text-creation-success-container-on-surface-default" />
                    <p className="font-ui font-body text-body-base text-creation-success-container-on-surface-default px-2 gap-3">
                        ผู้ป่วยยินยอมรับการตรวจและรักษาเมื่อวันที่ {dateStr} | {timeStr} น.
                    </p>
                </div>
            );
        } else {
            return (
                <div className="flex flex-row px-4 py-3 rounded-md bg-main-background-accented-warning">
                    <Icon name="exclamation-circle" size="md" className="text-creation-warning-container-on-surface-default" />
                    <p className="font-ui font-body text-body-base text-creation-warning-container-on-surface-default px-2 gap-3">
                        ผู้ป่วยยังไม่ได้กดยินยอมรับการตรวจและรักษา
                        {!readOnly && (
                            <>
                                <span className="underline cursor-pointer text-creation-link-container-on-surface-default ml-3 px-2 gap-3" onClick={handleAcceptConsent}>ยอมรับ</span>
                                <span className="underline cursor-pointer text-creation-link-container-on-surface-default ml-3 px-2 gap-3" onClick={handleRequestConsent}>เรียกหน้าหนังสือยินยอมใหม่</span>
                            </>
                        )}
                    </p>
                </div>
            );
        }
    }

    function buildFooter() {
        if (readOnly) {
            return (
                <div className="flex flex-row justify-between items-center gap-3">
                    {buildConsentSection()}
                    <div className="flex flex-row gap-3 ml-auto">
                        <Button
                            id="close-screening"
                            hierarchy="secondary"
                            size="md"
                            text="ปิด"
                            className="w-[120px] h-[40px]"
                            onClick={handleCancel}
                        />
                    </div>
                </div>
            );
        }

        return (
            <div className="flex flex-row justify-between items-center gap-3">
                {buildConsentSection()}
                <div className="flex flex-row gap-3 ml-auto">
                    <Button
                        id="draft-screening"
                        hierarchy="secondary"
                        size="md"
                        text="แบบร่าง"
                        className="w-[120px] h-[40px]"
                        onClick={handleDraft}
                    />
                    <Button
                        id="save-screening"
                        hierarchy="primary"
                        size="md"
                        text="บันทึกและนำเข้าคิว"
                        className="h-[40px]"
                        onClick={handleSubmit}
                        disabled={hasBlockingVitalSignsErrors}
                    />
                </div>
            </div>
        );
    }

    if (!isOpen) return null;

    if (showNoConsentModal) {
        return (
            <NoPatientConsentModal
                isOpen={true}
                onClose={() => setShowNoConsentModal(false)}
            />
        );
    }

    return (
        <div className="modal-scroll modal-scroll-no-outer">
            <div className="modal-dialog bg-white w-[990px] rounded-xl p-5 gap-6 flex flex-col">
                {buildHeader()}
                <div className="modal-body-scroll w-full gap-5 flex flex-col">
                    {buildComplaintSection()}
                    <hr className="border-main-border-system-default" />
                    {buildVitalSignsSection()}
                    <hr className="border-main-border-system-default" />
                    {buildLifestyleRows()}
                    <hr className="border-main-border-system-default" />
                    {buildMedicalCertificatesRow()}
                    {buildPackagesCollapsibleSection()}
                </div>
                <hr className="border-main-border-system-default" />
                {buildFooter()}
            </div>
        </div>
    );
}
